import type {AiTextMorphResponse} from '@PosterWhiteboard/poster/poster.types';
import {getPosterObjectFromPosterBackendObject} from '@PosterWhiteboard/poster/poster-backend';
import type {PosterMorphResponse} from '@PosterWhiteboard/poster/poster-backend.types';
import {applySmartColorPalette} from '@PosterWhiteboard/libraries/smart-color-palette.library';
import md5 from 'md5';

export const morphTextItemsForAiPrompt = async (
  itemKeyToTextMapping: Record<string, string>,
  prompt: string,
  promptFilters: Record<string, string> = {}
): Promise<AiTextMorphResponse> => {
  return window.PMW.readLocal('posterdesignmorph/generateTextForAiTextMorph', {
    mapping: itemKeyToTextMapping,
    prompt,
    promptFilters,
  }) as Promise<AiTextMorphResponse>;
};

export const morphPosterForAiPrompt = async (
  hashedId: string,
  prompt: string,
  promptFilters: Record<string, string> = {},
  includeImages = false,
  includeText = true,
  includeStyles = false
): Promise<PosterMorphResponse> => {
  const pageDataUrl = await window.posterEditor?.whiteboard?.getCurrentPage()?.toDataURL({
    maxDimension: 500,
  });

  if (pageDataUrl === undefined) {
    throw new Error('Failed to get page data URL for morphing poster');
  }

  const params = {
    designPreviewChecksum: md5(pageDataUrl),
    posterHashId: hashedId,
    prompt,
    filter: promptFilters,
    includeImages: includeImages ? '1' : '0',
    includeText: includeText ? '1' : '0',
    includeStyles: includeStyles ? '1' : '0',
    // TODO: Handle multiple pages
    designPreviewDataUrl: pageDataUrl,
  };

  const pollingKey = (await window.PMW.writeLocal('posterdesignmorph/morphPosterContent', params)) as {pollingKey: string};
  const backendResponse = (await window.PMW.pollingAjaxCallAsync(
    async (): Promise<void> => {},
    async (): Promise<PosterMorphResponse> => {
      return window.PMW.readLocal('posterdesignmorph/checkMorphPosterStatus', pollingKey);
    }
  )) as PosterMorphResponse;

  const colorPalette = backendResponse.colorPalette;
  const posterBackendObject = backendResponse.poster;

  return {
    poster: posterBackendObject,
    colorPalette: colorPalette,
    morphId: backendResponse.morphId,
  };
};

export const morphPoster = async (
  hashedId: string,
  prompt: string,
  promptFilters: Record<string, string> = {},
  includeImages: boolean,
  includeText: boolean,
  includeStyles: boolean
): Promise<PosterMorphResponse | undefined> => {
  if (!window.posterEditor?.whiteboard) {
    return;
  }

  let posterHashId = hashedId;
  if (!window.posterEditor?.whiteboard?.id) {
    await window.posterEditor?.whiteboard?.savePoster.save();
    posterHashId = window.posterEditor?.whiteboard?.hashedID;
  }

  const morphResponse = await morphPosterForAiPrompt(posterHashId, prompt, promptFilters, includeImages, includeText, includeStyles);
  const posterObject = getPosterObjectFromPosterBackendObject(morphResponse.poster);

  await window.posterEditor.whiteboard.updateFromObject(posterObject, {undoable: !morphResponse.colorPalette, fireModifiedEvent: false});

  if (morphResponse.colorPalette) {
    await applySmartColorPalette(morphResponse.colorPalette);
  }

  return morphResponse;
};
